import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { Header } from './shared/layout/header/header';
import { Footer } from './shared/layout/footer/footer';
import { GlobalLoadingComponent } from './shared/components/global-loading/global-loading.component';
import { AuthModalsHostComponent } from './shared/components/auth-modals/auth-modals-host/auth-modals-host.component';
import { ReturnUrlService } from './shared/services/return-url.service';
import { SessionService } from './shared/services/session.service';
import { SupervisionBannerComponent } from './shared/components/supervision-banner/supervision-banner.component';

const isStoreRoute = (url: string): boolean => /^\/store(\/|$)/.test(url.split(/[?#]/)[0]);

// Dentro de /store solo cambia el contenido del layout: no se vuelve al tope de la página.
export const isInternalStoreNavigation = (origen: string, destino: string): boolean =>
  isStoreRoute(origen) && isStoreRoute(destino);

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    Header,
    Footer,
    GlobalLoadingComponent,
    AuthModalsHostComponent,
    SupervisionBannerComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private readonly router = inject(Router);
  private readonly returnUrl = inject(ReturnUrlService);
  private readonly session = inject(SessionService);
  private urlPrevia = this.router.url;
  private primeraNavegacion = true;
  readonly fullScreen = signal(this.isFullScreenRoute());

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(({ urlAfterRedirects }) => {
        this.volverTrasGoogle(urlAfterRedirects);
        this.fullScreen.set(this.isFullScreenRoute());
        if (!isInternalStoreNavigation(this.urlPrevia, urlAfterRedirects)) {
          window.scrollTo(0, 0);
        }
        this.urlPrevia = urlAfterRedirects;
      });
  }

  // Google saca al usuario de la app y lo devuelve con la página recargada, así que el
  // regreso a su vista se resuelve en el primer ruteo del arranque: si la sesión quedó
  // restaurada y hay un destino guardado, se navega ahí.
  private volverTrasGoogle(url: string): void {
    if (!this.primeraNavegacion) {
      return;
    }
    this.primeraNavegacion = false;
    if (!this.session.usuario()) {
      return;
    }
    const destino = this.returnUrl.consumir();
    if (destino && destino !== url) {
      this.router.navigateByUrl(destino);
    }
  }

  private isFullScreenRoute(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return (
      path.startsWith('/login') ||
      path === '/auth/supervision' ||
      /^\/profile(\/|$)/.test(path)
    );
  }
}
